import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import CameraModal from '../components/CameraModal';
import DeliveryMap from '../components/DeliveryMap';
import { offersAPI, purchasesAPI, Offer } from '../api';
import { getCurrentLocation, geocodeLocation } from '../utils/geolocation';

interface Sale {
  id: number;
  listing_id: number;
  buyer_id: number;
  seller_id: number;
  delivery_address: string;
  status: string;
  created_at: string;
  title?: string;
  price?: number;
  item_price?: number;
  image_url?: string;
  buyer_name?: string;
  buyer_email?: string;
  delivery_photo?: string;
  estimated_arrival?: string;
}

const PAGE_SIZE = 20;

const MySales: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState<Sale[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cameraModal, setCameraModal] = useState<{
    isOpen: boolean;
    purchaseId: number;
  } | null>(null);
  const [etaModal, setEtaModal] = useState<{
    isOpen: boolean;
    purchaseId: number;
  } | null>(null);
  const [etaInput, setEtaInput] = useState({ date: '', time: '' });
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [trackingLocation, setTrackingLocation] = useState<{ [key: number]: boolean }>({});
  const [sellerLocation, setSellerLocation] = useState<{ [key: number]: { lat: number; lng: number } }>({});
  const [deliveryCoords, setDeliveryCoords] = useState<{ [key: number]: { lat: number; lng: number } }>({});
  const [estimatedTime, setEstimatedTime] = useState<{ [key: number]: number }>({}); // in minutes
  const watchIdRef = useRef<{ [key: number]: number }>({});

  useEffect(() => {
    loadSales();
    loadOffers();
  }, []);

  // Cleanup location tracking on unmount
  useEffect(() => {
    return () => {
      Object.keys(watchIdRef.current).forEach(purchaseId => {
        stopLocationTracking(parseInt(purchaseId));
      });
    };
  }, []);

  const loadOffers = async () => {
    setLoadingOffers(true);
    try {
      const data = await offersAPI.getBySeller();
      setOffers(data);
    } catch (err: any) {
      console.error('Error loading offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  const loadSales = async (append = false) => {
    if (append) setLoadingMore(true);
    else { setOffset(0); setHasMore(true); }
    try {
      const fetchOffset = append ? offset : 0;
      const data = await purchasesAPI.getMySales({ limit: PAGE_SIZE, offset: fetchOffset });
      if (append) {
        setSales(prev => [...prev, ...(data || [])]);
      } else {
        setSales(data || []);
      }
      setHasMore(Array.isArray(data) && data.length >= PAGE_SIZE);
      setOffset(fetchOffset + (Array.isArray(data) ? data.length : 0));
    } catch (err: any) {
      setError(err.message || 'Failed to load sales');
    } finally {
      setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  const updateStatus = async (purchaseId: number, newStatus: string, deliveryPhoto?: string, estimatedArrival?: string) => {
    try {
      await purchasesAPI.updateStatus(purchaseId, newStatus, deliveryPhoto, estimatedArrival);

      // Stop location tracking if order is delivered or cancelled
      if (newStatus === 'delivered' || newStatus === 'cancelled') {
        stopLocationTracking(purchaseId);
      }

      // Reload sales
      loadSales();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Estimate time based on distance (assuming average speed of 30 mph in city)
  const estimateTimeFromDistance = (distanceMiles: number): number => {
    const averageSpeed = 30; // mph
    const timeHours = distanceMiles / averageSpeed;
    return Math.ceil(timeHours * 60); // Convert to minutes
  };

  // Start tracking seller's location for navigation
  const startLocationTracking = async (purchaseId: number, deliveryAddress: string) => {
    if (!navigator.geolocation) {
      toast.error('Location services are not available');
      return;
    }

    setTrackingLocation(prev => ({ ...prev, [purchaseId]: true }));

    // Geocode delivery address using Nominatim
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(deliveryAddress)}&limit=1`,
        {
          headers: {
            'User-Agent': 'BuyDrop/1.0'
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
        setDeliveryCoords(prev => ({ ...prev, [purchaseId]: coords }));
      }
    } catch (err) {
      console.error('Error geocoding delivery address:', err);
    }

    // Watch position
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSellerLocation(prev => ({ ...prev, [purchaseId]: { lat: latitude, lng: longitude } }));

        // Calculate distance and update ETA
        const delivery = deliveryCoords[purchaseId];
        if (delivery) {
          const distance = calculateDistance(latitude, longitude, delivery.lat, delivery.lng);
          const timeMinutes = estimateTimeFromDistance(distance);
          setEstimatedTime(prev => ({ ...prev, [purchaseId]: timeMinutes }));

          // Auto-update ETA if order is shipped
          const sale = sales.find(s => s.id === purchaseId);
          if (sale && sale.status === 'shipped' && timeMinutes > 0) {
            const etaDate = new Date();
            etaDate.setMinutes(etaDate.getMinutes() + timeMinutes);
            // Update ETA in background (don't show toast for every update)
            updateStatus(purchaseId, 'shipped', undefined, etaDate.toISOString()).catch(() => {
              // Silently fail - don't spam user with errors
            });
          }
        }
      },
      (error) => {
        console.error('Location tracking error:', error);
        toast.error('Failed to track location');
        stopLocationTracking(purchaseId);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      }
    );

    watchIdRef.current[purchaseId] = watchId;
  };

  // Stop tracking location
  const stopLocationTracking = (purchaseId: number) => {
    if (watchIdRef.current[purchaseId]) {
      navigator.geolocation.clearWatch(watchIdRef.current[purchaseId]);
      delete watchIdRef.current[purchaseId];
    }
    setTrackingLocation(prev => {
      const newState = { ...prev };
      delete newState[purchaseId];
      return newState;
    });
  };

  const handleSetETA = () => {
    if (!etaInput.date || !etaInput.time) {
      toast.error('Please enter both date and time');
      return;
    }

    // Combine date and time into ISO string (using local timezone)
    const dateTimeString = `${etaInput.date}T${etaInput.time}:00`;
    const localDate = new Date(dateTimeString);
    const estimatedArrival = localDate.toISOString();

    if (etaModal) {
      // If order is already shipped, just update the ETA without changing status
      const sale = sales.find(s => s.id === etaModal.purchaseId);
      if (sale && sale.status === 'shipped') {
        // Just update ETA for already shipped orders
        updateStatus(etaModal.purchaseId, 'shipped', undefined, estimatedArrival);
      } else {
        // Mark as shipped with ETA
        updateStatus(etaModal.purchaseId, 'shipped', undefined, estimatedArrival);
      }
      setEtaModal(null);
      setEtaInput({ date: '', time: '' });
    }
  };

  const handleCapturePhoto = (imageDataUrl: string) => {
    if (cameraModal) {
      updateStatus(cameraModal.purchaseId, 'delivered', imageDataUrl);
      setCameraModal(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '📋';
    }
  };

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return 'Free';
    return `$${price.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading your sales...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">My Sales</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage orders for your listings</p>
        </div>

        {/* Fraud Warning */}
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="ri-alert-line text-xl sm:text-2xl text-red-600"></i>
            </div>
            <div className="ml-2 sm:ml-3">
              <h3 className="text-xs sm:text-sm font-semibold text-red-800 mb-1">
                ⚠️ Fraud Warning
              </h3>
              <p className="text-xs sm:text-sm text-red-700">
                Fraudulent activity will be reported to local authorities. All transactions are monitored, and any suspicious behavior, including but not limited to fake listings, payment fraud, or delivery scams, will result in immediate account suspension and legal action.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Offers Section */}
        {offers.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Pending Offers ({offers.filter(o => o.status === 'pending').length})</h2>
            <div className="space-y-4">
              {offers.filter(o => o.status === 'pending').map((offer) => (
                <div
                  key={offer.id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white hover:shadow-md transition"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2 gap-2">
                        <Link
                          to={`/listings/${offer.listing_id}`}
                          className="font-semibold text-base sm:text-lg text-gray-900 hover:text-green-600 break-words"
                        >
                          {offer.listing_title}
                        </Link>
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded self-start sm:self-auto">
                          Pending
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-2 gap-1 sm:gap-0">
                        <span className="font-bold text-lg sm:text-xl text-green-600">
                          ${offer.offer_amount.toLocaleString()}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">
                          Listing: ${offer.listing_price?.toLocaleString() || 'N/A'}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">
                        From: <span className="font-medium">{offer.buyer_name}</span>
                      </p>
                      {offer.message && (
                        <p className="text-xs sm:text-sm text-gray-700 mt-2 italic break-words">"{offer.message}"</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(offer.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 sm:ml-4">
                        <button
                          onClick={async () => {
                            try {
                              await offersAPI.accept(offer.id);
                              toast.success('Offer accepted! Purchase created.');
                              loadOffers();
                              loadSales();
                            } catch (err: any) {
                              const errorMsg = err.response?.data?.error || 'Failed to accept offer';
                              if (err.response?.data?.requiresAddress) {
                                // Old offer without delivery address - prompt for it
                                const deliveryAddress = prompt('This offer was created before delivery addresses were required. Please enter the delivery address:');
                                if (!deliveryAddress) return;
                                try {
                                  await offersAPI.accept(offer.id, deliveryAddress);
                                  toast.success('Offer accepted! Purchase created.');
                                  loadOffers();
                                  loadSales();
                                } catch (retryErr: any) {
                                  toast.error(retryErr.response?.data?.error || 'Failed to accept offer');
                                }
                              } else {
                                toast.error(errorMsg);
                              }
                            }
                          }}
                          className="w-full sm:w-auto px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition"
                        >
                          Accept
                        </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to reject this offer?')) return;
                          try {
                            await offersAPI.reject(offer.id);
                            loadOffers();
                          } catch (err: any) {
                            toast.error(err.response?.data?.error || 'Failed to reject offer');
                          }
                        }}
                        className="w-full sm:w-auto px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sales.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-12 text-center">
            <div className="text-4xl sm:text-6xl mb-4">💰</div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">No sales yet</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">When someone purchases your listings, they'll appear here</p>
            <button
              onClick={() => navigate('/create')}
              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-6 rounded-lg transition text-sm sm:text-base"
            >
              Create a Listing
            </button>
          </div>
        ) : (
          <>
          <div className="space-y-3 sm:space-y-4">
            {sales.map((sale) => (
              <div key={sale.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-3 sm:gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                      {sale.image_url ? (
                        <img
                          src={sale.image_url}
                          alt={sale.title}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-2xl sm:text-3xl">📦</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 break-words">
                          {sale.title || 'Item'}
                        </h3>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-xs sm:text-sm text-gray-600">
                            Buyer: <span className="font-medium">{sale.buyer_name || 'Unknown'}</span>
                          </p>
                          <button
                            onClick={() => navigate('/messages', { 
                              state: { 
                                buyerId: sale.buyer_id,
                                buyerName: sale.buyer_name || 'Buyer',
                                listingId: sale.listing_id,
                                listingTitle: sale.title,
                                listingImage: sale.image_url
                              } 
                            })}
                            className="flex-shrink-0 bg-green-500 hover:bg-green-600 text-white font-medium py-1.5 px-3 rounded-lg transition text-xs flex items-center space-x-1"
                            title="Message Buyer"
                          >
                            <i className="ri-message-2-line"></i>
                            <span>Message</span>
                          </button>
                        </div>
                        <p className="text-base sm:text-lg font-bold text-blue-600">
                          {formatPrice(sale.item_price || sale.price)}
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 sm:px-4 py-2 rounded-lg border ${getStatusColor(sale.status)} self-start sm:self-auto`}>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg sm:text-xl">{getStatusIcon(sale.status)}</span>
                        <span className="font-semibold capitalize text-sm sm:text-base">{sale.status}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3 sm:pt-4 mt-3 sm:mt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Order Date</p>
                        <p className="font-medium text-sm sm:text-base break-words">{formatDate(sale.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Delivery Address</p>
                        <p className="font-medium text-sm sm:text-base break-words">{sale.delivery_address}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Buyer Contact</p>
                        <p className="font-medium text-sm sm:text-base break-words">{sale.buyer_email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Order ID</p>
                        <p className="font-medium text-sm sm:text-base text-gray-600">#{sale.id}</p>
                      </div>
                    </div>

                    {/* Message Buyer Button */}
                    <div className="mt-3 sm:mt-4 mb-3 sm:mb-4">
                      <button
                        onClick={() => navigate('/messages', { 
                          state: { 
                            buyerId: sale.buyer_id,
                            buyerName: sale.buyer_name || 'Buyer',
                            listingId: sale.listing_id,
                            listingTitle: sale.title,
                            listingImage: sale.image_url
                          } 
                        })}
                        className="w-full sm:w-auto px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition text-sm font-medium flex items-center justify-center space-x-2"
                      >
                        <span>💬</span>
                        <span>Message Buyer</span>
                      </button>
                    </div>

                    {/* Status Update Buttons - Flow Based */}
                    <div className="border-t pt-3 sm:pt-4 mt-3 sm:mt-4">
                      <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Update Order Status:</p>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                        {sale.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(sale.id, 'processing')}
                              className="w-full sm:w-auto px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition text-sm font-medium"
                            >
                              Start Processing
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to cancel this order?')) {
                                  updateStatus(sale.id, 'cancelled');
                                }
                              }}
                              className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-sm font-medium"
                            >
                              Cancel Order
                            </button>
                          </>
                        )}
                        {sale.status === 'processing' && (
                          <button
                            onClick={async () => {
                              // Set default ETA to 2 hours from now
                              const defaultDate = new Date();
                              defaultDate.setHours(defaultDate.getHours() + 2);
                              const dateStr = defaultDate.toISOString().split('T')[0];
                              const timeStr = defaultDate.toTimeString().split(':').slice(0, 2).join(':');
                              setEtaInput({ date: dateStr, time: timeStr });
                              setEtaModal({ isOpen: true, purchaseId: sale.id });
                            }}
                            className="w-full sm:w-auto px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition text-sm font-medium"
                          >
                            Mark as Shipped
                          </button>
                        )}
                        {sale.status === 'shipped' && (
                          <>
                            {!trackingLocation[sale.id] ? (
                              <>
                                <button
                                  onClick={() => {
                                    startLocationTracking(sale.id, sale.delivery_address);
                                  }}
                                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm font-medium"
                                >
                                  🗺️ Navigate to Location
                                </button>
                                <button
                                  onClick={() => {
                                    // If there's an existing ETA, pre-fill it
                                    if (sale.estimated_arrival) {
                                      const etaDate = new Date(sale.estimated_arrival);
                                      const dateStr = etaDate.toISOString().split('T')[0];
                                      const timeStr = etaDate.toTimeString().split(':').slice(0, 2).join(':');
                                      setEtaInput({ date: dateStr, time: timeStr });
                                    } else {
                                      // Default to 2 hours from now
                                      const defaultDate = new Date();
                                      defaultDate.setHours(defaultDate.getHours() + 2);
                                      const dateStr = defaultDate.toISOString().split('T')[0];
                                      const timeStr = defaultDate.toTimeString().split(':').slice(0, 2).join(':');
                                      setEtaInput({ date: dateStr, time: timeStr });
                                    }
                                    setEtaModal({ isOpen: true, purchaseId: sale.id });
                                  }}
                                  className="w-full sm:w-auto px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition text-sm font-medium"
                                >
                                  ✏️ Change ETA
                                </button>
                              </>
                            ) : (
                              <>
                                {deliveryCoords[sale.id] && sellerLocation[sale.id] && (
                                  <div className="w-full mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs sm:text-sm text-blue-800 mb-1">
                                      📍 Tracking your location...
                                    </p>
                                    <p className="text-xs text-blue-600">
                                      Distance: {calculateDistance(
                                        sellerLocation[sale.id].lat,
                                        sellerLocation[sale.id].lng,
                                        deliveryCoords[sale.id].lat,
                                        deliveryCoords[sale.id].lng
                                      ).toFixed(1)} miles
                                      {estimatedTime[sale.id] && ` • ETA: ${estimatedTime[sale.id]} min`}
                                    </p>
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    stopLocationTracking(sale.id);
                                    setCameraModal({ isOpen: true, purchaseId: sale.id });
                                  }}
                                  className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm font-medium"
                                >
                                  ✅ I've Arrived - Mark as Delivered
                                </button>
                                <button
                                  onClick={() => stopLocationTracking(sale.id)}
                                  className="w-full sm:w-auto px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition text-sm font-medium"
                                >
                                  Stop Tracking
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {sale.status === 'delivered' && (
                          <p className="text-xs sm:text-sm text-green-600 font-medium">
                            ✅ Order completed successfully!
                          </p>
                        )}
                        {sale.status === 'cancelled' && (
                          <p className="text-xs sm:text-sm text-red-600 font-medium">
                            ❌ This order has been cancelled
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status Messages */}
                    {sale.status === 'pending' && (
                      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-yellow-800">
                          ⏳ New order received! Please prepare the item for delivery.
                        </p>
                      </div>
                    )}

                    {sale.status === 'processing' && (
                      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-blue-800">
                          🔄 Order is being processed. Prepare the item and mark as shipped when ready.
                        </p>
                      </div>
                    )}

                    {sale.status === 'shipped' && (
                      <>
                        <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <p className="text-xs sm:text-sm text-purple-800">
                            📦 Item is on the way! Mark as delivered when the buyer receives it.
                          </p>
                        </div>
                        {trackingLocation[sale.id] && deliveryCoords[sale.id] && sellerLocation[sale.id] && (
                          <div className="mt-4">
                            <DeliveryMap
                              deliveryAddress={sale.delivery_address}
                              sellerLocation={sellerLocation[sale.id]}
                              status={sale.status}
                              orderDate={sale.created_at}
                            />
                            {estimatedTime[sale.id] && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs sm:text-sm text-blue-800 text-center">
                                  Estimated arrival: {estimatedTime[sale.id]} minutes
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {sale.status === 'delivered' && (
                      <div className="mt-3 sm:mt-4 rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm">
                        {/* Delivery photo - DoorDash style */}
                        {sale.delivery_photo && (
                          <div className="relative aspect-video w-full bg-gray-100">
                            <img
                              src={sale.delivery_photo}
                              alt="Delivery confirmation"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-white">
                              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              </span>
                              <span className="font-semibold text-sm drop-shadow">Delivered</span>
                            </div>
                          </div>
                        )}
                        <div className="px-4 py-3 bg-green-50 border-b border-green-100">
                          <p className="text-xs sm:text-sm font-medium text-green-800">
                            ✅ Order completed! The buyer has received their item.
                          </p>
                        </div>
                        <div className="p-4 space-y-4">
                          {/* Item + Buyer + Address + Amount */}
                          <div className="flex gap-3">
                            {sale.image_url ? (
                              <img src={sale.image_url} alt={sale.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-2xl">📦</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{sale.title || 'Item'}</p>
                              <p className="text-sm text-gray-500">Buyer: {sale.buyer_name || 'Unknown'}</p>
                              <p className="text-base font-bold text-gray-900 mt-1">
                                {formatPrice(sale.item_price || sale.price)}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2 pt-3 border-t border-gray-100">
                            <div>
                              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Delivered to</p>
                              <p className="text-sm font-medium text-gray-900">{sale.buyer_name || 'Buyer'}</p>
                              <p className="text-sm text-gray-600">{sale.delivery_address}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Order total</p>
                              <p className="text-lg font-bold text-gray-900">
                                {formatPrice(sale.item_price || sale.price)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => loadSales(true)}
                disabled={loadingMore}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-70 text-white font-medium rounded-lg transition flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
          </>
        )}

        {cameraModal && (
          <CameraModal
            isOpen={cameraModal.isOpen}
            onClose={() => setCameraModal(null)}
            onCapture={handleCapturePhoto}
            title="Take Delivery Photo"
          />
        )}

        {/* ETA Modal */}
        {etaModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Set Estimated Arrival Time</h2>
              <p className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm">
                When will this order arrive at the delivery address?
              </p>
              {sales.find(s => s.id === etaModal.purchaseId)?.estimated_arrival && (
                <div className="mb-3 sm:mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs sm:text-sm text-blue-800">
                  Current ETA: {new Date(sales.find(s => s.id === etaModal.purchaseId)!.estimated_arrival!).toLocaleString()}
                </div>
              )}
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={etaInput.date}
                    onChange={(e) => setEtaInput({ ...etaInput, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={etaInput.time}
                    onChange={(e) => setEtaInput({ ...etaInput, time: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6">
                <button
                  onClick={() => {
                    setEtaModal(null);
                    setEtaInput({ date: '', time: '' });
                  }}
                  className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetETA}
                  className="w-full sm:flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition text-sm"
                >
                  Set ETA & Mark Shipped
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySales;

